'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/lib/hooks';
import { setActiveView } from '@/lib/slices/login-slice';
import type { ActiveView } from '@/lib/slices/login-slice';
import { useRouter } from 'next/navigation';
import HomeViewAccessService from '@/services/auth/home-view-access-service';
import FeatureTrackingService from '@/services/analytics/feature-tracking-service';

const VIEW_LABELS: Record<ActiveView, string> = {
  admin: 'Admin View',
  instructor: 'Instructor View',
  student: 'Student View',
};

// TODO: Re-enable once admin-to-student dashboard permissions are defined.
const ENABLE_ADMIN_STUDENT_VIEW_SWAP = false;

export default function RoleToggle(): React.JSX.Element | null {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { role: userRole, effectiveRole } = useAppSelector((state) => state.isLoggedIn);
  const activeView = useAppSelector((state) => state.isLoggedIn.activeView) as ActiveView | null;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const availableViews = HomeViewAccessService.getAvailableViews(effectiveRole, userRole).filter(
    (view) => view !== 'student' || ENABLE_ADMIN_STUDENT_VIEW_SWAP,
  );

  if (availableViews.length <= 1) return null;
  if (!activeView) return null;

  const handleSwitch = (view: ActiveView) => {
    if (!availableViews.includes(view)) {
      return;
    }

    if (view !== activeView) {
      FeatureTrackingService.captureEvent('active_role', { mode: view });
      if (view === 'admin') {
        FeatureTrackingService.captureEvent('institute_overview_switched_to_admin');
      } else if (view === 'instructor') {
        FeatureTrackingService.captureEvent('institute_overview_switched_to_instructor');
      }
    }

    dispatch(setActiveView(view));
    sessionStorage.setItem('active_view', view);
    setIsOpen(false);
    if (view === 'admin') {
      router.push('/admin');
    } else if (view === 'instructor') {
      router.push('/admin');
    } else if (view === 'student') {
      router.push('/student');
    }
  };

  return (
    <div className='relative' ref={dropdownRef}>
      <div className='flex items-center gap-2'>
        {/* Blue clock icon box */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            borderRadius: '8px',
            background: '#1570EF',
            padding: '6px',
            boxShadow: '0 1px 2px 0 rgba(10,13,18,0.05)',
          }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
          >
            <g clipPath='url(#clip0_2386_2642)'>
              <path
                d='M9.99984 5.00001V10L13.3332 11.6667M18.3332 10C18.3332 14.6024 14.6022 18.3333 9.99984 18.3333C5.39746
                    18.3333 1.6665 14.6024 1.6665 10C1.6665 5.39763 5.39746 1.66667 9.99984 1.66667C14.6022 1.66667 18.3332 
                    5.39763 18.3332 10Z'
                stroke='white'
                strokeWidth='1.67'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </g>
            <defs>
              <clipPath id='clip0_2386_2642'>
                <rect width='20' height='20' fill='white' />
              </clipPath>
            </defs>
          </svg>
        </div>

        <span className='text-xs text-text_gray_500 dark:text-gray-400 hidden sm:block'>
          Active Role:
        </span>

        <button
          type='button'
          onClick={() => setIsOpen((prev) => !prev)}
          className='flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border-2 border-border_gray_300 dark:border-gray-600 bg-primary/5 dark:bg-[#1a1a1a] rounded-lg hover:bg-primary/10 dark:hover:bg-zinc-800 transition-colors'
        >
          {activeView && VIEW_LABELS[activeView]}
          <svg
            className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div
          className='absolute right-0 top-[calc(100%+6px)] z-50 bg-surface_white dark:bg-[#1a1a1a] border border-border_gray_200
                        dark:border-gray-700 rounded-xl shadow-lg w-44 py-1'
        >
          {availableViews.map((view) => (
            <button
              key={view}
              type='button'
              onClick={() => handleSwitch(view)}
              className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                activeView === view
                  ? 'text-primary font-semibold bg-surface_gray_50 dark:bg-zinc-800'
                  : 'text-text_gray_700 dark:text-gray-300 hover:bg-surface_gray_50 dark:hover:bg-zinc-800'
              }`}
            >
              {VIEW_LABELS[view]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
